import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { NamePollForm, POLL_INTRO } from "@/components/NamePoll";
import { NEW_FROM_POLL_KEY, recall, forget, setReturnTo } from "@/lib/returnTo";

// Shareable link to the name poll (Jack, 2026-10-19): playjangle.com/poll.
// Opens the poll straight away, without the lobby's finished-a-game rule.
// Votes are one per account, so signed-out visitors sign in or sign up first
// and are brought back here (lib/returnTo.js, read by Login and Register).

export default function Poll() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [poll, setPoll] = useState(null);
  const [mine, setMine] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/name-poll/me").then(({ data }) => {
      setPoll(data);
      if (data.voted) setMine(data.ranking || []);
    }, () => setError(true));
  }, [user]);

  // New players who signed up from this link carry on to the usual
  // new-player steps after voting.
  const next = recall(NEW_FROM_POLL_KEY) ? "/avatar?welcome=1" : "/lobby";
  const goOn = () => {
    forget(NEW_FROM_POLL_KEY);
    navigate(next, { replace: true });
  };

  let body;
  if (loading) {
    body = <p className="text-muted-foreground">Loading…</p>;
  } else if (!user) {
    body = (
      <>
        <p className="text-muted-foreground">
          We need your help to pick a new name for the game. Sign in or make a free account to vote. Everyone gets
          one vote.
        </p>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            asChild
            className="bg-brand hover:bg-brand-hover text-brand-foreground rounded-full px-6"
            onClick={() => setReturnTo("/poll")}
          >
            <Link to="/register">Create account</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full px-6" onClick={() => setReturnTo("/poll")}>
            <Link to="/login">Sign in</Link>
          </Button>
        </div>
      </>
    );
  } else if (error) {
    body = <p className="text-sm text-red-500">Couldn't load the poll. Please try again.</p>;
  } else if (!poll) {
    body = <p className="text-muted-foreground">Loading…</p>;
  } else if (mine) {
    body = (
      <>
        <p className="text-muted-foreground">Thanks for voting! {mine.length ? "Your top picks:" : ""}</p>
        {mine.length > 0 && (
          <ol className="grid gap-1 text-sm">
            {mine.map((n, i) => (
              <li key={n} className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-brand text-brand-foreground flex items-center justify-center text-xs font-bold">
                  {i + 1}
                </span>
                <span className="font-semibold">{n}</span>
              </li>
            ))}
          </ol>
        )}
        <div className="pt-2">
          <Button onClick={goOn} className="bg-brand hover:bg-brand-hover text-brand-foreground rounded-full px-6">
            Play Jangle
          </Button>
        </div>
      </>
    );
  } else {
    body = (
      <>
        <p className="text-muted-foreground">{POLL_INTRO(poll.picks)}</p>
        <NamePollForm poll={poll} onVoted={(r) => setMine(r || [])} />
      </>
    );
  }

  return (
    <main className="min-h-[calc(100vh-4rem)] flex items-start sm:items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-surface border border-border rounded-2xl p-5 sm:p-7 flex flex-col gap-4" data-testid="poll-page">
        <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight">Help us pick a new name!</h1>
        {body}
      </div>
    </main>
  );
}
